import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { clsx } from 'clsx';

interface SettingsModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    icon?: React.ElementType;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    children: React.ReactNode;
    footer?: React.ReactNode;
}

export function SettingsModal({
    open, onClose, title, subtitle, icon: Icon, size = 'md', children, footer
}: SettingsModalProps) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className={clsx(
                            'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-transparent dark:border-slate-800 rounded-3xl shadow-2xl w-full max-h-[85vh] overflow-hidden flex flex-col',
                            size === 'sm' ? 'max-w-sm' :
                                size === 'lg' ? 'max-w-2xl' :
                                    size === 'xl' ? 'max-w-4xl' :
                                        'max-w-lg'
                        )}
                    >
                        {/* Fixed Header */}
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                {Icon && (
                                    <div className="p-1.5 rounded-lg bg-white/20">
                                        <Icon size={18} className="text-white" />
                                    </div>
                                )}
                                <div>
                                    <h2 className="text-lg font-bold text-white">{title}</h2>
                                    {subtitle && <p className="text-emerald-100 text-xs">{subtitle}</p>}
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                                type="button"
                            >
                                <X size={16} className="text-white" />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6">{children}</div>

                        {/* Fixed Footer */}
                        {footer && (
                            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-950/60 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3 flex-shrink-0">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}